import { useState } from "react";
import SourceHealthBadge from "../SourceHealthBadge";
import AdminTableContainer from "../layout/AdminTableContainer";
import AdminBtn from "../layout/AdminBtn";
import { DATA_SOURCE_SCHEDULE_INTERVALS } from "./dataSourceScheduleUtils";
import {
  resolveRunNowUnavailableReason,
  resolveScheduleUnavailableReason,
} from "./dataSourceActionAvailability";
import DeleteDataSourceModal from "./DeleteDataSourceModal";
import EditDataSourceModal from "./EditDataSourceModal";
import { collectionSummary, lastResultSummary } from "./dataSourceManageCopy";
import {
  canStopDataSource,
  canToggleDataSource,
  stopDataSourceRun,
  toggleDataSourceEnabled,
} from "./dataSourceDetailActions";
import RunningElapsedLabel from "./RunningElapsedLabel";
import {
  buildDataSourceDetailHref,
  navigateToDataSourceDetail,
} from "../../../../utils/dataSourceDetailRoutes";

function EditModalForSource({
  state,
  canManageSources,
  isSaving,
  intervals,
  canEditEnabled,
  canEditSchedule,
  viewRunsLabel,
  runHelp,
  extraActions,
  renderSourceExtras,
  deleteDomain,
  saveError,
  onClose,
  onSave,
  onRunNow,
  onViewRuns,
  onStop,
  onDelete,
}) {
  const { current, isBusy, isRunning, isStopping, runBlockedReason, scheduleBlockedReason } = state;
  const canStop =
    canManageSources &&
    (canStopDataSource(current) ||
      (isRunning &&
        (current.domainId === "real_estate" || current.domainId === "vehicle_market")));
  return (
    <EditDataSourceModal
      source={current}
      canManage={canManageSources}
      isSaving={isSaving}
      intervals={intervals}
      canEditEnabled={canEditEnabled && canManageSources}
      canEditSchedule={canEditSchedule && canManageSources && !scheduleBlockedReason}
      scheduleBlockedReason={scheduleBlockedReason}
      runBlockedReason={runBlockedReason}
      runHelp={runBlockedReason ? "" : runHelp}
      viewRunsLabel={viewRunsLabel}
      deleteBlockedReason=""
      saveError={saveError}
      canStop={canStop}
      stopBusy={Boolean(isStopping)}
      extraActions={
        <>
          {renderSourceExtras?.(current)}
          {extraActions?.(current, { isBusy, isRunning })}
        </>
      }
      onClose={onClose}
      onSave={onSave}
      onRunNow={canStop ? null : onRunNow}
      onViewRuns={onViewRuns}
      onStop={canStop ? onStop : null}
      onDelete={deleteDomain && canManageSources ? onDelete : null}
    />
  );
}

const DataSourceControlsGrid = ({
  sources,
  emptyMessage = "No sources match the current filters.",
  busyKey,
  canManageSources,
  onToggleEnabled,
  onRunNow,
  onSaveSchedule,
  onViewRuns,
  extraActions,
  viewRunsLabel = "Run history",
  resolveRunNowTitle,
  renderSourceExtras,
  deleteDomain,
  onDeleted,
  onRefresh,
  showToast,
  sectionLabel,
  scheduleIntervals = DATA_SOURCE_SCHEDULE_INTERVALS,
}) => {
  const [editSource, setEditSource] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editSaveError, setEditSaveError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [togglingKey, setTogglingKey] = useState(null);
  const [stoppingKey, setStoppingKey] = useState(null);

  if (!sources?.length) {
    return <p className="muted">{emptyMessage}</p>;
  }

  const foodGroups = new Set(
    (sources || []).map((source) => source.extraSourceLines?.[0]).filter(Boolean)
  );
  const heading =
    sectionLabel ||
    (sources[0]?.domainLabel === "Food"
      ? foodGroups.size === 1
        ? [...foodGroups][0]
        : "Food"
      : sources[0]?.domainLabel);

  const liveSource = (source) =>
    sources.find((row) => row.sourceKey === source?.sourceKey) || source;

  const rowState = (source) => {
    const current = liveSource(source);
    const isBusy = busyKey === current.sourceKey || busyKey === current.sourceCode;
    const isStopping =
      stoppingKey === current.sourceKey || stoppingKey === current.sourceCode;
    const isRunning =
      current.health === "running" ||
      current.health === "queued" ||
      Boolean(current.isRunning) ||
      Boolean(current.activeRunId);
    return {
      current,
      isBusy,
      isStopping,
      isRunning,
      runBlockedReason: resolveRunNowUnavailableReason(current, {
        canManageSources,
        isBusy,
        isRunning,
      }),
      scheduleBlockedReason: resolveScheduleUnavailableReason(current, {
        canManageSources,
        isBusy,
      }),
    };
  };

  const openDetail = (source) => {
    if (!source?.domainId || !source?.sourceKey) return;
    navigateToDataSourceDetail(source.domainId, source.sourceKey);
  };

  const handleStop = async (source) => {
    const current = liveSource(source);
    const running =
      current.health === "running" ||
      current.health === "queued" ||
      Boolean(current.isRunning) ||
      Boolean(current.activeRunId);
    if (
      !(
        canStopDataSource(current) ||
        (running &&
          (current.domainId === "real_estate" || current.domainId === "vehicle_market"))
      )
    ) {
      return;
    }
    const key = current.sourceKey || current.sourceCode;
    setStoppingKey(key);
    try {
      const result = await stopDataSourceRun(current);
      showToast?.(
        result?.message || `Stop requested for ${current.displayName}.`,
        "success"
      );
      await onRefresh?.({ silent: true });
    } catch (error) {
      showToast?.(error.message || `Unable to stop ${current.displayName}.`, "error");
    } finally {
      setStoppingKey(null);
    }
  };

  const handleToggleEnabled = async (source) => {
    const current = liveSource(source);
    const key = current.sourceKey || current.sourceCode;
    if (onToggleEnabled) {
      await onToggleEnabled(current);
      return;
    }
    if (!canToggleDataSource(current)) {
      showToast?.("This source does not have an on/off switch.", "error");
      return;
    }
    const nextEnabled = !current.enabled;
    setTogglingKey(key);
    try {
      await toggleDataSourceEnabled(current, nextEnabled);
      showToast?.(
        nextEnabled ? `${current.displayName} enabled.` : `${current.displayName} disabled.`,
        "success"
      );
      onRefresh?.({ silent: true });
    } catch (error) {
      showToast?.(error.message || "Unable to update source.", "error");
    } finally {
      setTogglingKey(null);
    }
  };

  const handleSave = async (source, draft) => {
    const { current, isRunning } = rowState(source);
    const nextEnabled = draft.enabledChanged ? draft.enabled : Boolean(current.enabled);
    setEditSaving(true);
    setEditSaveError("");
    try {
      if (draft.enabledChanged) {
        try {
          if (canToggleDataSource(current)) {
            await toggleDataSourceEnabled(current, draft.enabled);
            showToast?.(
              draft.enabled
                ? `${current.displayName} enabled.`
                : `${current.displayName} disabled.`,
              "success"
            );
            await onRefresh?.({ silent: true });
          } else if (onToggleEnabled) {
            // Domain row toggle flips current state; only safe when draft differs.
            await onToggleEnabled(current);
            await onRefresh?.({ silent: true });
          } else {
            throw new Error("This source does not have an on/off switch.");
          }
        } catch (error) {
          showToast?.(error?.message || "Unable to update source.", "error");
          throw error;
        }
      }
      if ((draft.scheduleChanged || draft.schedulePayload?.runImmediately) && onSaveSchedule) {
        // Domain handlers toast before rethrowing on failure.
        await onSaveSchedule(current, draft.schedulePayload);
      }
      setEditSource(null);
      setEditSaveError("");
      setEditSaving(false);
      // Never await Run now here — real-estate polling can take many minutes and
      // would freeze the Manage dialog as if the page had locked up.
      if (draft.runImmediately && onRunNow && nextEnabled && !isRunning) {
        void onRunNow({ ...current, enabled: nextEnabled });
      }
    } catch (error) {
      setEditSaveError(error?.message || "Unable to save source changes.");
      setEditSaving(false);
    }
  };

  return (
    <>
      {heading ? <h2 className="admin-ds-section-title">{heading}</h2> : null}
      <AdminTableContainer>
        <table className="admin-data-health-table admin-re-controls-table admin-ds-manage-table">
          <thead>
            <tr>
              <th>Source</th>
              <th>Status</th>
              <th>Enabled</th>
              <th>Collection</th>
              <th>Last result</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sources.map((source) => {
              const health = source.health || "never_run";
              const isRunning =
                health === "running" ||
                health === "queued" ||
                Boolean(source.isRunning) ||
                Boolean(source.activeRunId);
              const canToggle = Boolean(onToggleEnabled) || canToggleDataSource(source);
              const isToggling =
                togglingKey === source.sourceKey ||
                togglingKey === source.sourceCode ||
                busyKey === source.sourceKey ||
                busyKey === source.sourceCode;
              const isStopping =
                stoppingKey === source.sourceKey || stoppingKey === source.sourceCode;
              const canStop =
                canManageSources &&
                (canStopDataSource(source) ||
                  (isRunning &&
                    (source.domainId === "real_estate" || source.domainId === "vehicle_market")));
              const toggleBlockedReason = !canManageSources
                ? "Requires permission to manage sources."
                : !canToggle
                  ? "This source does not have an on/off switch."
                  : isToggling
                    ? "Updating this source…"
                    : "";
              return (
                <tr
                  key={source.sourceKey}
                  className={`${isRunning ? "admin-re-row--running" : ""}${
                    source.domainId ? " admin-ds-manage-table__row--openable" : ""
                  }`}
                  onClick={(event) => {
                    if (event.target.closest("a, button, input, label, .admin-ds-enable")) {
                      return;
                    }
                    openDetail(source);
                  }}
                >
                  <td className="admin-re-source-cell">
                    {source.domainId ? (
                      <a
                        className="admin-ds-source-name-link"
                        href={buildDataSourceDetailHref(source.domainId, source.sourceKey)}
                        onClick={(event) => {
                          event.preventDefault();
                          openDetail(source);
                        }}
                      >
                        {source.displayName}
                      </a>
                    ) : (
                      <strong>{source.displayName}</strong>
                    )}
                    {source.domainLabel ? (
                      <div className="muted admin-re-source-code">{source.domainLabel}</div>
                    ) : null}
                    {source.attribution ? (
                      <div className="muted admin-re-source-code">{source.attribution}</div>
                    ) : null}
                  </td>
                  <td>
                    <div className="admin-ds-status-with-elapsed">
                      <SourceHealthBadge status={health} />
                      <RunningElapsedLabel source={source} isRunning={isRunning} />
                    </div>
                  </td>
                  <td>
                    <label
                      className="admin-ds-enable"
                      title={toggleBlockedReason || (source.enabled ? "Collection is on" : "Collection is off")}
                    >
                      <input
                        type="checkbox"
                        role="switch"
                        checked={Boolean(source.enabled)}
                        disabled={Boolean(toggleBlockedReason)}
                        aria-label={
                          source.enabled
                            ? `Disable ${source.displayName}`
                            : `Enable ${source.displayName}`
                        }
                        onClick={(event) => event.stopPropagation()}
                        onChange={(event) => {
                          event.stopPropagation();
                          if (toggleBlockedReason) return;
                          handleToggleEnabled(source);
                        }}
                      />
                      <span>{source.enabled ? "On" : "Off"}</span>
                    </label>
                  </td>
                  <td>{collectionSummary(source)}</td>
                  <td>
                    <div>{lastResultSummary(source)}</div>
                    {source.lastErrorMessage ? (
                      <div className="muted admin-re-source-code admin-re-run-error">
                        {source.lastErrorMessage.length > 90
                          ? `${source.lastErrorMessage.slice(0, 90)}…`
                          : source.lastErrorMessage}
                      </div>
                    ) : null}
                  </td>
                  <td className="admin-re-actions-cell">
                    {canStop ? (
                      <AdminBtn
                        variant="danger"
                        disabled={isStopping || isToggling}
                        title="Stop the in-progress sync at the next safe checkpoint"
                        onClick={() => handleStop(source)}
                      >
                        {isStopping ? "Stopping…" : "Stop"}
                      </AdminBtn>
                    ) : null}
                    <AdminBtn
                      variant="secondary"
                      onClick={() => {
                        setEditSaveError("");
                        setEditSource(source);
                      }}
                    >
                      {canManageSources ? "Manage" : "View"}
                    </AdminBtn>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </AdminTableContainer>
      {editSource ? (
        <EditModalForSource
          state={rowState(editSource)}
          canManageSources={canManageSources}
          isSaving={editSaving}
          intervals={scheduleIntervals}
          canEditEnabled={
            Boolean(onToggleEnabled) || canToggleDataSource(rowState(editSource).current)
          }
          canEditSchedule={Boolean(onSaveSchedule)}
          viewRunsLabel={viewRunsLabel}
          runHelp={resolveRunNowTitle?.(rowState(editSource).current)}
          extraActions={extraActions}
          renderSourceExtras={renderSourceExtras}
          deleteDomain={deleteDomain}
          saveError={editSaveError}
          onClose={() => {
            if (editSaving) return;
            setEditSource(null);
            setEditSaveError("");
          }}
          onSave={(draft) => handleSave(editSource, draft)}
          onRunNow={
            onRunNow
              ? () => {
                  const current = rowState(editSource).current;
                  setEditSource(null);
                  setEditSaveError("");
                  onRunNow(current);
                }
              : null
          }
          onViewRuns={
            onViewRuns
              ? () => {
                  const current = rowState(editSource).current;
                  setEditSource(null);
                  setEditSaveError("");
                  onViewRuns(current);
                }
              : null
          }
          onStop={() => handleStop(rowState(editSource).current)}
          onDelete={
            deleteDomain && canManageSources
              ? () => setDeleteTarget(rowState(editSource).current)
              : null
          }
        />
      ) : null}
      {deleteTarget ? (
        <DeleteDataSourceModal
          source={deleteTarget}
          domain={deleteDomain}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => {
            const removed = deleteTarget;
            setDeleteTarget(null);
            setEditSource(null);
            onDeleted?.(removed);
          }}
        />
      ) : null}
    </>
  );
};

export default DataSourceControlsGrid;
